import React, { useMemo } from 'react';

import noop from 'lodash/noop';

import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';
import Tooltip from '@atlaskit/tooltip';

const MAX_AVATARS_FEATURED_BOTTOM = 4;
const MAX_AVATARS_FEATURED_TOP = 2;

type AvatarFlagPosition = 'top' | 'bottom';
type Props = Omit<AvatarGroupProps, 'onMoreClick'> & { position?: AvatarFlagPosition };

/*
 * The original AkAvatarGroup cannot be used inside an AkFlag, because the More button (e.g. `+2`)
 * opens a popup whose z-index is too low, and gets displayed below the flag.
 */
export const FlagCompatibleAvatarGroup = ({ position = 'bottom', data, ...props }: Props) => {
	const maxAvatarCount =
		position === 'top' ? MAX_AVATARS_FEATURED_TOP : MAX_AVATARS_FEATURED_BOTTOM;
	const tooltipPosition = position;
	const { data: customData, overrides: customOverrides } = useMemo(
		() => getCustomAvatarGroupProps({ data, maxAvatarCount }),
		[data, maxAvatarCount],
	);

	const avatarGroup = (
		<AvatarGroup
			maxCount={maxAvatarCount}
			data={customData}
			overrides={customOverrides}
			boundariesElement="scrollParent"
			tooltipPosition={tooltipPosition}
			onMoreClick={noop}
			{...props}
		/>
	);

	return avatarGroup;
};

type ProcessedUsers = (AvatarGroupProps['data'][0] | { key: string; name: string })[];

export const getCustomAvatarGroupProps = ({
	data: users,
	maxAvatarCount = MAX_AVATARS_FEATURED_BOTTOM,
}: Pick<AvatarGroupProps, 'data'> & { maxAvatarCount?: number }): {
	data: ProcessedUsers;
	overrides: AvatarGroupProps['overrides'];
} => {
	const lastFeaturedUser = maxAvatarCount - 1;
	const overflowUsers = users.slice(lastFeaturedUser);
	const overrides: AvatarGroupProps['overrides'] = {
		MoreIndicator: {
			render: (Component, componentProps) => (
				<Tooltip content={overflowUsers.map((u) => u.name).join(', ')} position="top">
					<Component {...componentProps} borderColor="none" onClick={noop} />
				</Tooltip>
			),
		},
	};
	return { overrides, data: users };
};
